import './CombinedCircleCard.scss';
import { Spinner } from "react-bootstrap";
import React, { useEffect, useRef, useState } from 'react';

interface CombinedCircleCardProps {
  refresh?: boolean;
  title?: string;
  percentage?: number;
  data?: number;
  color?: string | number;
  bgColor?: string;
  units?: string;
  circleType?: 'info' | 'progress';
  strokeWidth?: number;
  minSize?: number;
}

const CombinedCircleCard: React.FC<CombinedCircleCardProps> = (props) => {
  const {
    title,
    refresh = false,
    circleType = 'info',
    percentage = 0,
    data,
    units = '',
    color = 1, 
    bgColor = 'var(--background-secondary)',
    strokeWidth = 10,
    minSize = 80 // Mida mínima del cercle per pantalles petites
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [circleSize, setCircleSize] = useState(0);
  const [dataCircleRadius, setDataCircleRadius] = useState(110);

  // Funció per determinar el color segons l'opció passada per props
  const getColor = () => {
    if (typeof color === 'string') {
      return color;
    }
    
    switch (color) {
      case 1:
        return '#19DAAD'; // Verd
      case 2:
        return '#F5A623'; // Taronja
      case 3:
        return '#CF594D'; // Vermell
      case 4:
        return '#728aff'; // Blau
      default:
        return '#19DAAD'; // Verd per defecte
    }
  };

  // Calcular la mida del cercle en funció del tamany total del contenidor
  useEffect(() => {
    const calculateSize = () => {
      if (containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const containerWidth = containerRef.current.clientWidth;
        
        // Deixem espai per al títol si n'hi ha
        const availableHeight = title ? containerHeight - 40 : containerHeight;
        
        // Aproximadament un 80% del contenidor, però mai més petit que minSize
        const maxSize = Math.max(
          minSize, 
          Math.min(availableHeight, containerWidth) * 0.8
        );
        
        setCircleSize(maxSize);
        setDataCircleRadius(maxSize / 2 - strokeWidth);
      }
    };

    // Calcula la mida inicial
    calculateSize();
    
    // Recalcula quan canvia la mida de la finestra
    window.addEventListener('resize', calculateSize);
    
    return () => {
      window.removeEventListener('resize', calculateSize);
    };
  }, [title, strokeWidth, minSize]);

  // Càlcul per al cercle de progrés
  const calculateProgressCircle = () => {
    const normalizedRadius = dataCircleRadius;
    const circumference = normalizedRadius * 2 * Math.PI;
    
    // Assegurar que el progrés estigui entre 0 i 100
    const boundedData = Math.min(Math.max(0, percentage), 100);
    
    // Calcular el dashoffset basat en el progrés
    const dashoffset = circumference - (boundedData / 100) * circumference;
    
    return {
      normalizedRadius,
      circumference,
      dashoffset
    };
  };

  // Obtenir valors per al cercle de progrés
  const { normalizedRadius, circumference, dashoffset } = calculateProgressCircle();

  // Determinar el valor a mostrar
  const displayValue = circleType === 'progress' ? percentage : data;

  return (
    <div 
      className="combined-circle-card p-2 rounded-1" 
      style={{ backgroundColor: bgColor }}
      ref={containerRef}
    >
      {title && (
        <p className="card-title h4 text-center mt-1 mb-2">
          {title}
        </p>
      )}
      
      <div className="data-circle-container">
        {refresh ? (
          <div className="spinner-container">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <div className="circle-wrapper">
            <div 
              className="data-circle" 
              style={{ 
                height: `${circleSize}px`, 
                width: `${circleSize}px` 
              }}
            >
              <svg viewBox="0 0 240 240">
                {circleType === 'info' ? (
                  // Cercle d'informació simple
                  <circle
                    stroke={getColor()}
                    fill="transparent"
                    strokeWidth={strokeWidth}
                    r={normalizedRadius}
                    cx={120}
                    cy={120}
                  />
                ) : (
                  // Cercle de progrés
                  <>
                    {/* Cercle de fons */}
                    <circle
                      stroke="rgba(255, 255, 255, 0.1)"
                      fill="transparent"
                      strokeWidth={strokeWidth}
                      r={normalizedRadius}
                      cx={120}
                      cy={120}
                    />
                    {/* Cercle de progrés */}
                    <circle
                      stroke={getColor()}
                      fill="transparent"
                      strokeWidth={strokeWidth}
                      strokeDasharray={`${circumference} ${circumference}`}
                      style={{ strokeDashoffset: dashoffset }}
                      strokeLinecap="round"
                      r={normalizedRadius}
                      cx={120}
                      cy={120}
                      className="progress-circle"
                      transform="rotate(-90 120 120)"
                    />
                  </>
                )}
              </svg>
              <div className="data-content" style={{ color: getColor() }}>
                <div className="data-value">
                  {displayValue !== undefined ? displayValue.toFixed(0) : '0'}
                  {circleType === 'progress' && (
                    <span className="progress-percent">%</span>
                  )}
                  {units && <span className="data-unit">{units}</span>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CombinedCircleCard;